import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircle, UserPlus, Clock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

function connKind(status) {
  const s = String(status || "none").toLowerCase();
  if (s === "self" || s === "me") return "self";
  if (s === "connected" || s === "friends") return "connected";
  if (s === "none" || s === "stranger") return "none";
  if (s.includes("incoming") || s.endsWith("_in") || s === "received" || s === "awaiting_you") return "incoming";
  return "pending"; // pending_outgoing / requested / sent
}

// Connection-aware CTA (mobile §16.2 parity via GET /connections/status/{id}):
// shows Message / Respond / Requested / Connect — never offers "Connect" to someone
// you're already connected to or have a pending request with.
export function ConnectionButton({ user, onDone }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const id = user?.id;
  const label = user?.username || user?.handle || "user";

  const { data, isLoading } = useQuery({
    queryKey: ["conn-status", id],
    queryFn: () => api.connectionStatus(id),
    enabled: !!id,
    staleTime: 30000,
  });
  const kind = connKind(data?.status);

  const openChat = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      const chat = await api.openChat(id);
      onDone?.();
      navigate(`/app/chats/${chat.id || chat.conversation_id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.error === "dm_gate_unmet") toast.error(d.message || "This person only accepts DMs from token holders.");
      else toast.error((typeof d === "string" && d) || d?.message || "Couldn't open the chat.");
    } finally {
      setBusy(false);
    }
  };

  const connect = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      await api.sendConnectionRequest(id);
      toast.success(`Connection request sent to @${label}`);
      qc.setQueryData(["conn-status", id], { status: "pending_outgoing" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send request.");
    } finally {
      setBusy(false);
    }
  };

  if (!id || kind === "self") return null;

  return (
    <span className="shrink-0" data-testid={`conn-action-${label}`}>
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-bloq-muted" />
      ) : kind === "connected" ? (
        <button onClick={openChat} disabled={busy} className="flex items-center gap-1 rounded-full border border-bloq-mint/40 bg-bloq-mint/10 px-3 py-1.5 text-xs font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/15 disabled:opacity-50" data-testid={`msg-btn-${label}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><MessageCircle className="h-3.5 w-3.5" /> Message</>}
        </button>
      ) : kind === "incoming" ? (
        <button onClick={(e) => { e.stopPropagation(); navigate("/app/requests"); }} className="flex items-center gap-1 rounded-full border border-bloq-blue/40 bg-bloq-blue/10 px-3 py-1.5 text-xs font-semibold text-bloq-blue transition-colors hover:bg-bloq-blue/15" data-testid={`respond-btn-${label}`}>
          <UserPlus className="h-3.5 w-3.5" /> Respond
        </button>
      ) : kind === "pending" ? (
        <span className="flex items-center gap-1 rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted" data-testid={`pending-badge-${label}`}>
          <Clock className="h-3.5 w-3.5" /> Requested
        </span>
      ) : (
        <button onClick={connect} disabled={busy} className="flex items-center gap-1 rounded-full bloq-gradient px-3 py-1.5 text-xs font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-50" data-testid={`connect-btn-${label}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><UserPlus className="h-3.5 w-3.5" /> Connect</>}
        </button>
      )}
    </span>
  );
}
